import type { DocsFormDataMapped, DocsFormName } from "core/types";
import { format } from "date-fns";
import { renderToStaticMarkup } from "react-dom/server";
import Translations from "translations/types";
import { PdfCreatorProps } from "..";

export type FieldConfig<T extends DocsFormName> = {
  label: (translations: Translations) => string;
  key: keyof DocsFormDataMapped<T>;
};

type Section = { label: string; number: number; value: string };

function DischargeLetterSection({ label, number, value }: Section) {
  return (
    <div className="mb-4">
      <div className="mb-1 text-body-lg font-bold">
        {number}. {label}
      </div>
      <div className="whitespace-pre-wrap break-words">{value}</div>
    </div>
  );
}

function DischargeLetterDocument<T extends DocsFormName>({
  createdBy,
  fieldConfig,
  formData,
  locale,
  translations,
}: PdfCreatorProps<T> & { fieldConfig: Array<FieldConfig<T>> }) {
  const title = translations.extractDocs.dischargeLetter.generatedPdf.title;
  const createdByText =
    translations.extractDocs.dischargeLetter.generatedPdf.createdBy({
      date: format(new Date(), "dd.MM.yyyy, HH:mm", { locale }),
      name: createdBy ?? "Recare",
    });

  const sections: Section[] = fieldConfig
    .filter((field) => {
      const value = formData[field.key];
      return value != null && String(value).trim() !== "";
    })
    .map((field, index) => ({
      label: field.label(translations),
      number: index + 1,
      value: String(formData[field.key] ?? "").trim(),
    }));

  return (
    <div className="max-w-[210mm] p-6 font-sans leading-snug text-black">
      <header className="mb-6 flex items-start justify-between">
        <div className="flex-1">
          <h1 className="m-0 text-heading-xl font-bold">{title}</h1>
        </div>
      </header>
      <main>
        {sections.map((section) => (
          <DischargeLetterSection
            key={section.number}
            label={section.label}
            number={section.number}
            value={section.value}
          />
        ))}
      </main>
      <footer className="mt-8 border-t border-neutral-200 pt-3 text-[9pt] text-neutral-500">
        {createdByText}
      </footer>
    </div>
  );
}

export async function createDischargeLetterPdf<T extends DocsFormName>(
  props: PdfCreatorProps<T>,
  fieldConfig: Array<FieldConfig<T>>,
): Promise<Uint8Array> {
  const html = renderToStaticMarkup(
    <DischargeLetterDocument {...props} fieldConfig={fieldConfig} />,
  );
  const container = document.createElement("div");
  container.innerHTML = html;

  const html2pdf = (await import("html2pdf.js")).default;
  const arrayBuffer = await html2pdf()
    .set({
      html2canvas: {
        foreignObjectRendering: false,
        removeContainer: true,
        scale: 1.0,
      },
      image: { quality: 0.75, type: "jpeg" },
      jsPDF: { format: "a4", unit: "mm" },
    })
    .from(container)
    .outputPdf("arraybuffer");

  return new Uint8Array(arrayBuffer as ArrayBuffer);
}
